import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Plus, Trash2, Edit2, Check, X, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import config from './config';

/* const API_BASE_URL = 'https://prenotazioni.mitconsulting.eu/api';*/
/* const API_BASE_URL = 'https://localhost/booking-system/api'; */
const API_BASE_URL = config.API_BASE_URL;
const ADMIN_EMAIL = config.ADMIN_EMAIL;


const apiCall = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export default function RoomBookingSystem() {
  // Stati principali
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('login');
  const [editingBooking, setEditingBooking] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [weekView, setWeekView] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [showResetPassword, setShowResetPassword] = useState(false);
  
  // Stato per stanza selezionata
  const [selectedRoom, setSelectedRoom] = useState(() => {
    const enabledRooms = config.ROOMS.filter(room => room.enabled);
    return enabledRooms.length > 0 ? enabledRooms[0].id : null;
  });
  
  // Form states
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    nome: '', cognome: '', professione: '', email: '', password: '', cellulare: ''
  });
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    date: '', 
    startTime: '09:00', 
    endTime: '09:30', 
    note: '',
    room_id: null, // CAMBIATO: inizia sempre come null
    repeatWeekly: false,
    repeatWeeks: 4,
	admin_notes: ''
  });
  
  // UI states
  const [emailStatus, setEmailStatus] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null });
  const [alertDialog, setAlertDialog] = useState({ show: false, message: '' });
  const [userInfoDialog, setUserInfoDialog] = useState({ show: false, user: null });
  const [loading, setLoading] = useState(false);
  
  // Filtri Admin
  const [adminFilters, setAdminFilters] = useState({
    dateFrom: '',
    dateTo: '',
    userId: 'all'
  });
  const [selectedBookings, setSelectedBookings] = useState([]);

  // Funzioni utility per stanze
  const getEnabledRooms = () => {
    return config.ROOMS.filter(room => room.enabled);
  };

  const getRoomById = (room_id) => {
    return config.ROOMS.find(r => r.id === room_id);
  };

  const isRoomEnabled = (room_id) => {
    const room = getRoomById(room_id);
    return room && room.enabled;
  };

  // Funzioni utility generali
  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmDialog({ show: true, message, onConfirm });
  };

  const showAlert = (message) => {
    setAlertDialog({ show: true, message });
  };

  const showUserInfo = (user) => {
    setUserInfoDialog({ show: true, user });
  };

  const timeToMinutes = (time) => {
    const cleanTime = time.substring(0, 5);
    const parts = cleanTime.split(':').map(Number);
    return parts[0] * 60 + parts[1];
  };

  const minutesToTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let i = config.WORKING_HOURS_START; i < config.WORKING_HOURS_END; i += config.TIME_SLOT_INTERVAL) {
      slots.push(minutesToTime(i));
    }
    return slots;
  };
  const generateEndTimeSlots = () => {
    const slots = [];
    for (let i = config.WORKING_HOURS_START; i <= config.WORKING_HOURS_END; i += config.TIME_SLOT_INTERVAL) {
      slots.push(minutesToTime(i));
    }
    return slots;
  };
  const formatDate = (date) => {
    const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const formatDateForDisplay = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const changeWeek = (weeks) => {
    const newWeek = new Date(currentWeekStart);
    newWeek.setDate(newWeek.getDate() + (weeks * 7));
    setCurrentWeekStart(newWeek);
	// Refresh prenotazioni quando cambi settimana
    //loadBookings();
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getBookingsForDate = (date, room_id = null) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const ds = `${year}-${month}-${day}`;
    
    let filtered = bookings.filter(b => {
      if (b.date !== ds) return false;
      if (b.room_id && !isRoomEnabled(b.room_id)) return false;
      return true;
    });
    
    if (room_id && room_id !== 'all') {
      filtered = filtered.filter(b => b.room_id === room_id);
    }
    
    return filtered.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
  };

  // API calls
  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await apiCall('users.php');
      setUsers(data);
    } catch (error) {
      showAlert('Errore caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await apiCall('bookings.php');
      setBookings(data);
    } catch (error) {
      showAlert('Errore caricamento prenotazioni');
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async (to, subject, body) => {
    try {
      const response = await apiCall('send-email.php', {
        method: 'POST',
        body: JSON.stringify({
          to: to,
          subject: subject,
          body: body,
          from: config.SUPPORT_EMAIL,
          replyTo: config.SUPPORT_EMAIL
        })
      });
      
      if (response.success) {
        console.log('Email inviata con successo a:', to);
        setEmailStatus(`Email inviata a ${to}`);
        setTimeout(() => setEmailStatus(''), 3000);
      } else {
        console.error('Errore invio email:', response.message);
        setEmailStatus('Errore invio email');
        setTimeout(() => setEmailStatus(''), 3000);
      }
    } catch (error) {
      console.error('Errore chiamata API email:', error);
      setEmailStatus('Errore connessione server email');
      setTimeout(() => setEmailStatus(''), 3000);
    }
  };

  // useEffect principale
  useEffect(() => {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setCurrentUser(user);
        setView(user.isAdmin ? 'admin' : 'calendar');
      } catch (error) {
        console.error('Errore recupero utente:', error);
        localStorage.removeItem('currentUser');
      }
    }
    
    const savedWeekView = localStorage.getItem('weekView');
    if (savedWeekView !== null) {
      setWeekView(savedWeekView === 'true');
    }
    
    const savedDate = localStorage.getItem('selectedDate');
    if (savedDate) {
      try {
        const [year, month, day] = savedDate.split('-');
        setSelectedDate(new Date(year, month - 1, day));
      } catch (error) {
        console.error('Errore recupero data:', error);
      }
    }
    
    loadUsers();
    loadBookings();
    setCurrentWeekStart(getMonday(new Date()));
  }, []);

  // Auto-refresh prenotazioni ogni 30 secondi
  // useEffect(() => {
    // if (!currentUser) return;
    
    // const intervalId = setInterval(() => {
      // loadBookings();
      // if (view === 'admin') {
        // loadUsers();
      // }
    // }, 30000); // 30 secondi
    
    // return () => clearInterval(intervalId);
  // }, [currentUser, view]);

  // Refresh quando l'applicazione ottiene il focus
  useEffect(() => {
    if (!currentUser) return;

    const handleFocus = () => {
      console.log('App ha ottenuto il focus - refresh dati');
      loadBookings();
      if (view === 'admin') {
        loadUsers();
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [currentUser, view]);


  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('weekView', weekView.toString());
    }
  }, [weekView, currentUser]);

  useEffect(() => {
    if (currentUser) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      localStorage.setItem('selectedDate', dateStr);
    }
  }, [selectedDate, currentUser]);

  useEffect(() => {
    if (showBookingForm && !editingBooking) {
      setBookingForm(prev => ({ 
        ...prev, 
        date: (() => {
          const year = selectedDate.getFullYear();
          const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
          const day = String(selectedDate.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        })()
      }));
    }
  }, [selectedDate, showBookingForm, editingBooking]);
  
  useEffect(() => {
    if ((showBookingForm || editingBooking) && selectedRoom && selectedRoom !== 'all') {
      setBookingForm(prev => {
        if (prev.room_id !== selectedRoom) {
          return { ...prev, room_id: selectedRoom };
        }
        return prev;
      });
    }
  }, [selectedRoom, showBookingForm, editingBooking]);

  const handleLogin = async () => {
    if (!loginForm.email || !loginForm.password) {
      return showAlert('Inserisci email e password');
    }
    
    try {
      setLoading(true);
      const response = await apiCall('users.php', {
        method: 'POST',
        body: JSON.stringify({
          action: 'login',
          email: loginForm.email,
          password: loginForm.password
        })
      });
      
      if (response.success) {
        setCurrentUser(response.user);
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        setView(response.user.isAdmin ? 'admin' : 'calendar');
        setLoginForm({ email: '', password: '' });
      } else {
        showAlert('Email o password non corretti');
      }
    } catch (error) {
      showAlert('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!registerForm.nome || !registerForm.cognome || !registerForm.email || !registerForm.cellulare) {
      return showAlert('COMPILA TUTTI I CAMPI OBBLIGATORI');
    }
    
    try {
      setLoading(true);
      const response = await apiCall('users.php', {
        method: 'POST',
        body: JSON.stringify({
          action: 'register',
          ...registerForm
        })
      });
      
      if (response.success) {
        await loadUsers();
        sendEmail(config.ADMIN_EMAIL, 'Nuova registrazione', `${registerForm.nome} ${registerForm.cognome}`);
        showAlert('REGISTRAZIONE COMPLETATA! Riceverai una notifica via EMAIL o WHATSAPP quando il tuo account sara approvato.');
        setRegisterForm({ nome: '', cognome: '', professione: '', email: '', password: '', cellulare: '' });
        setView('login');
      } else {
        showAlert(response.error || 'Errore durante la registrazione');
      }
    } catch (error) {
      showAlert('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };
  const handleChangePassword = async () => {
    if (!changePasswordForm.currentPassword || !changePasswordForm.newPassword || !changePasswordForm.confirmPassword) {
      return showAlert('Compila tutti i campi');
    }
    
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      return showAlert('Le nuove password non coincidono');
    }
    
    if (changePasswordForm.newPassword.length < 6) {
      return showAlert('La password deve essere di almeno 6 caratteri');
    }
    
    try {
      setLoading(true);
      const response = await apiCall('users.php', {
        method: 'POST',
        body: JSON.stringify({
          action: 'change-password',
          userId: currentUser.id,
          currentPassword: changePasswordForm.currentPassword,
          newPassword: changePasswordForm.newPassword
        })
      });
      
      if (response.success) {
        showAlert('Password cambiata con successo!');
        setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowChangePassword(false);
      } else {
        showAlert(response.error || 'Errore durante il cambio password');
      }
    } catch (error) {
      showAlert('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };
  const handleResetPassword = async () => {
    if (!resetPasswordEmail) {
      return showAlert('Inserisci la tua email');
    }
    
    try {
      setLoading(true);
      const response = await apiCall('users.php', {
        method: 'POST',
        body: JSON.stringify({
          action: 'reset-password',
          email: resetPasswordEmail
        })
      });
      
      if (response.success) {
        // Invia email con nuova password
        sendEmail(
          resetPasswordEmail,
          'Reset Password',
          `Ciao ${response.userName},<br><br>
           La tua password è stata reimpostata.<br><br>
           <strong>Nuova password temporanea: ${response.tempPassword}</strong><br><br>
           Ti consigliamo di cambiarla dopo il primo accesso.<br><br>
           Puoi accedere qui: https://prenotazioni.mitconsulting.eu/`
        );
        
        showAlert('Password reimpostata! Controlla la tua email per la nuova password temporanea.');
        setResetPasswordEmail('');
        setShowResetPassword(false);
      } else {
        showAlert(response.error || 'Email non trovata');
      }
    } catch (error) {
      showAlert('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };  
  
  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('weekView');
    localStorage.removeItem('selectedDate');
    setView('login');
    setShowBookingForm(false);
    setEditingBooking(null);
    setWeekView(false);
    setAdminFilters({ dateFrom: '', dateTo: '', userId: 'all' });
    setSelectedBookings([]);
  };

  const approveUser = async (userId) => {
    try {
      setLoading(true);
      const response = await apiCall('users.php', {
        method: 'POST',
        body: JSON.stringify({
          action: 'approve',
          userId: userId
        })
      });
      
      if (response.success) {
        await loadUsers();
        const user = users.find(u => u.id === userId);
        if (user) {
          sendEmail(user.email, 'Registrazione Approvata', 'Account approvato! Per inserire le prenotazione delle stanze puoi utilizzare il seguente link https://prenotazioni.mitconsulting.eu/');
          showAlert(`Utente ${user.nome} approvato`);
        }
      }
    } catch (error) {
      showAlert('Errore approvazione utente');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      setLoading(true);
      await apiCall(`users.php?id=${userId}`, {
        method: 'DELETE'
      });
      
      await loadUsers();
      
      const userBookings = bookings.filter(b => b.userId === userId);
      for (const booking of userBookings) {
        await apiCall(`bookings.php?id=${booking.id}`, {
          method: 'DELETE'
        });
      }
      await loadBookings();
      
      showAlert('Utente eliminato!');
    } catch (error) {
      showAlert('Errore eliminazione utente');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelectedBookings = async () => {
    if (selectedBookings.length === 0) {
      return showAlert('Seleziona almeno una prenotazione');
    }
    
    showConfirm(`Eliminare ${selectedBookings.length} prenotazioni?`, async () => {
      try {
        setLoading(true);
        for (const bookingId of selectedBookings) {
          await apiCall(`bookings.php?id=${bookingId}`, {
            method: 'DELETE'
          });
        }
        await loadBookings();
        setSelectedBookings([]);
        showAlert(`${selectedBookings.length} prenotazioni eliminate!`);
      } catch (error) {
        showAlert('Errore eliminazione prenotazioni');
      } finally {
        setLoading(false);
      }
    });
  };

  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev.includes(bookingId)) {
        return prev.filter(id => id !== bookingId);
      } else {
        return [...prev, bookingId];
      }
    });
  };

  const toggleSelectAllBookings = (filteredBookings) => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.id));
    }
  };

  const getFilteredBookings = () => {
    let filtered = [...bookings];
    
    // Filtro per data inizio
    if (adminFilters.dateFrom) {
      filtered = filtered.filter(b => b.date >= adminFilters.dateFrom);
    }
    
    // Filtro per data fine
    if (adminFilters.dateTo) {
      filtered = filtered.filter(b => b.date <= adminFilters.dateTo);
    }
    
    // Filtro per utente 
    if (adminFilters.userId !== 'all') {
      filtered = filtered.filter(b => String(b.userId) === String(adminFilters.userId));
    }
    
    // Ordina per data e ora
    return filtered.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date.localeCompare(b.date);
      }
      return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
    });
  };

  const isTimeSlotAvailable = (date, startTime, endTime, room_id, excludeId = null) => {
    const d = new Date(date).getDay();
    if (d === 0 || d === 6) return false;
    const start = timeToMinutes(startTime);
    const end = timeToMinutes(endTime);
    if (start < config.WORKING_HOURS_START || end > config.WORKING_HOURS_END || end - start < config.MIN_BOOKING_DURATION) return false;
    
    const dayBookings = bookings.filter(b => b.date === date && b.room_id === room_id && b.id !== excludeId);
    
    for (const booking of dayBookings) {
      const bs = timeToMinutes(booking.startTime);
      const be = timeToMinutes(booking.endTime);
      if ((start >= bs - config.BOOKING_BUFFER_MINUTES && start < be + config.BOOKING_BUFFER_MINUTES) || 
          (end > bs - config.BOOKING_BUFFER_MINUTES && end <= be + config.BOOKING_BUFFER_MINUTES) || 
          (start <= bs && end >= be)) {
        return false;
      }
    }
    return true;
  };

  const handleCreateBooking = async () => {
    // Verifica che sia selezionata una stanza
    if (!bookingForm.room_id || bookingForm.room_id === 'all') {
      return showAlert('⚠️ Seleziona una stanza prima di procedere');
    }
    
    const sm = timeToMinutes(bookingForm.startTime);
    const em = timeToMinutes(bookingForm.endTime);
    const duration = em - sm;
    
    // Verifica che la durata sia multiplo di 30 minuti
    if (duration % 30 !== 0) {
      return showAlert('⚠️ DURATA NON VALIDA\n\nLa prenotazione deve essere di 30 minuti o suoi multipli.\n\nEsempi validi:\n• 30 minuti\n• 1 ora (60 min)\n• 1 ora e 30 minuti (90 min)\n• 2 ore (120 min)\n• ecc.\n\nEsempi NON validi:\n• 45 minuti\n• 1 ora e 15 minuti\n• 2 ore e 45 minuti');
    }
    
    if (duration < config.MIN_BOOKING_DURATION) {
      return showAlert(`⚠️ DURATA TROPPO BREVE\n\nDurata minima: ${config.MIN_BOOKING_DURATION} minuti\n\nLa tua prenotazione: ${duration} minuti`);
    }
    
    if (duration > config.MAX_BOOKING_DURATION) {
      return showAlert(`⚠️ DURATA TROPPO LUNGA\n\nDurata massima: ${config.MAX_BOOKING_DURATION / 60} ore\n\nLa tua prenotazione: ${duration / 60} ore`);
    }
    
    if (sm < config.WORKING_HOURS_START) {
      return showAlert(`⚠️ ORARIO TROPPO PRESTO\n\nOrario di apertura: ${minutesToTime(config.WORKING_HOURS_START)}\n\nIl tuo orario di inizio: ${bookingForm.startTime}`);
    }
    
    if (em > config.WORKING_HOURS_END) {
      return showAlert(`⚠️ ORARIO TROPPO TARDI\n\nOrario di chiusura: ${minutesToTime(config.WORKING_HOURS_END)}\n\nIl tuo orario di fine: ${bookingForm.endTime}`);
    }
    
    // Verifica disponibilità per ripetizioni settimanali
    if (bookingForm.repeatWeekly) {
      const unavailableDates = [];
      for (let week = 0; week < bookingForm.repeatWeeks; week++) {
        const weekDate = new Date(bookingForm.date);
        weekDate.setDate(weekDate.getDate() + (week * 7));
        const year = weekDate.getFullYear();
        const month = String(weekDate.getMonth() + 1).padStart(2, '0');
        const day = String(weekDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        if (!isTimeSlotAvailable(dateStr, bookingForm.startTime, bookingForm.endTime, bookingForm.room_id)) {
          unavailableDates.push(dateStr);
        }
      }
      
      if (unavailableDates.length > 0) {
        const roomName = getRoomById(bookingForm.room_id)?.name || 'la sala';
        return showAlert(`⚠️ ORARIO NON DISPONIBILE\n\n${roomName} non è disponibile nelle seguenti date:\n\n${unavailableDates.map(d => formatDateForDisplay(d)).join('\n')}\n\n📋 REGOLE PRENOTAZIONE:\n• Durata: multipli di 30 minuti\n• Orario: ${minutesToTime(config.WORKING_HOURS_START)} - ${minutesToTime(config.WORKING_HOURS_END)}\n• Buffer tra prenotazioni: ${config.BOOKING_BUFFER_MINUTES} minuti`);
      }
    } else {
      // Verifica singola prenotazione
      if (!isTimeSlotAvailable(bookingForm.date, bookingForm.startTime, bookingForm.endTime, bookingForm.room_id)) {
        const roomName = getRoomById(bookingForm.room_id)?.name || 'la sala';
        return showAlert(`⚠️ ORARIO NON DISPONIBILE\n\n${roomName} è già prenotata in questo orario o troppo vicino ad un'altra prenotazione.\n\n📋 REGOLE PRENOTAZIONE:\n• Durata: multipli di 30 minuti (30min, 1h, 1h30, 2h...)\n• Orario disponibile: ${minutesToTime(config.WORKING_HOURS_START)} - ${minutesToTime(config.WORKING_HOURS_END)}\n• Durata minima: ${config.MIN_BOOKING_DURATION} minuti\n• Durata massima: ${config.MAX_BOOKING_DURATION / 60} ore\n• Buffer richiesto: ${config.BOOKING_BUFFER_MINUTES} minuti tra prenotazioni\n\nScegli un altro orario o verifica il calendario.`);
      }
    }
    
    try {
      setLoading(true);
      
      // Crea prenotazioni
      const bookingsToCreate = [];
      const weeksCount = bookingForm.repeatWeekly ? bookingForm.repeatWeeks : 1;
      
      for (let week = 0; week < weeksCount; week++) {
        const weekDate = new Date(bookingForm.date);
        weekDate.setDate(weekDate.getDate() + (week * 7));
        const year = weekDate.getFullYear();
        const month = String(weekDate.getMonth() + 1).padStart(2, '0');
        const day = String(weekDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        bookingsToCreate.push({
          userId: currentUser.id,
          userName: `${currentUser.nome} ${currentUser.cognome}`,
          date: dateStr,
          startTime: bookingForm.startTime,
          endTime: bookingForm.endTime,
          note: bookingForm.note,
          room_id: bookingForm.room_id,
          admin_notes: bookingForm.admin_notes || ''
        });
      }
      
     // Invia tutte le prenotazioni
      for (const booking of bookingsToCreate) {
        await apiCall('bookings.php', {
          method: 'POST',
          body: JSON.stringify(booking)
        });
      }
      
      await loadBookings();
      
      // Invia email di notifica all'amministratore
      const roomName = getRoomById(bookingForm.room_id)?.name || 'la sala';
      const emailBody = bookingForm.repeatWeekly 
        ? `<strong>${currentUser.nome} ${currentUser.cognome}</strong> ha creato ${bookingsToCreate.length} prenotazioni per ${roomName}.<br><br>
           Primo appuntamento: ${formatDateForDisplay(bookingsToCreate[0].date)} dalle ${bookingForm.startTime} alle ${bookingForm.endTime}<br>
           Ultimo appuntamento: ${formatDateForDisplay(bookingsToCreate[bookingsToCreate.length-1].date)} dalle ${bookingForm.startTime} alle ${bookingForm.endTime}<br><br>
           Utente: ${currentUser.email}<br>
           Note: ${bookingForm.note || 'Nessuna'}`
        : `<strong>${currentUser.nome} ${currentUser.cognome}</strong> ha creato una nuova prenotazione per ${roomName}.<br><br>
           Data: ${formatDateForDisplay(bookingForm.date)}<br>
           Orario: dalle ${bookingForm.startTime} alle ${bookingForm.endTime}<br>
           Utente: ${currentUser.email}<br>
           Note: ${bookingForm.note || 'Nessuna'}`;
      
      sendEmail(
        config.ADMIN_EMAIL,
        'Nuova Prenotazione',
        emailBody
      );
      
      setShowBookingForm(false);
      setBookingForm({ 
        date: '', 
        startTime: '09:00', 
        endTime: '09:30', 
        note: '', 
        room_id: null,
        repeatWeekly: false,
        repeatWeeks: 4,
        admin_notes: ''
      });
      
      const message = bookingForm.repeatWeekly 
        ? `${bookingsToCreate.length} prenotazioni create con successo!` 
        : 'Prenotazione creata!';
      showAlert(message);


    } catch (error) {
      showAlert('Errore creazione prenotazione');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBooking = async () => {
    // Verifica che sia selezionata una stanza
    if (!bookingForm.room_id || bookingForm.room_id === 'all') {
      return showAlert('⚠️ Seleziona una stanza prima di procedere');
    }
    
    const sm = timeToMinutes(bookingForm.startTime);
    const em = timeToMinutes(bookingForm.endTime);
    const duration = em - sm;
    
    // Verifica che la durata sia multiplo di 30 minuti
    if (duration % 30 !== 0) {
      return showAlert('⚠️ DURATA NON VALIDA\n\nLa prenotazione deve essere di 30 minuti o suoi multipli.\n\nEsempi validi: 30min, 1h, 1h30, 2h, ecc.');
    }
    
    if (duration < config.MIN_BOOKING_DURATION) {
      return showAlert(`⚠️ DURATA TROPPO BREVE\n\nDurata minima: ${config.MIN_BOOKING_DURATION} minuti`);
    }
    
    if (duration > config.MAX_BOOKING_DURATION) {
      return showAlert(`⚠️ DURATA TROPPO LUNGA\n\nDurata massima: ${config.MAX_BOOKING_DURATION / 60} ore`);
    }
    
    if (!isTimeSlotAvailable(bookingForm.date, bookingForm.startTime, bookingForm.endTime, bookingForm.room_id, editingBooking.id)) {
      const roomName = getRoomById(bookingForm.room_id)?.name || 'la sala';
      return showAlert(`⚠️ ORARIO NON DISPONIBILE\n\n${roomName} è già prenotata in questo orario.\n\n📋 REGOLE:\n• Multipli di 30 minuti\n• Orario: ${minutesToTime(config.WORKING_HOURS_START)} - ${minutesToTime(config.WORKING_HOURS_END)}\n• Buffer: ${config.BOOKING_BUFFER_MINUTES} minuti`);
    }
    
    try {
      setLoading(true);
	  await apiCall('bookings.php', {
        method: 'PUT',
        body: JSON.stringify({
          id: editingBooking.id,
          date: bookingForm.date,
          startTime: bookingForm.startTime,
          endTime: bookingForm.endTime,
          note: bookingForm.note,
          room_id: bookingForm.room_id,
          admin_notes: bookingForm.admin_notes || ''
        })
      });
      
      await loadBookings();
      
      // Invia email di notifica all'amministratore con vecchia e nuova info
      const roomName = getRoomById(bookingForm.room_id)?.name || 'la sala';
      const oldRoomName = getRoomById(editingBooking.room_id)?.name || 'la sala';
      sendEmail(
        config.ADMIN_EMAIL,
        'Prenotazione Modificata',
        `<strong>${currentUser.nome} ${currentUser.cognome}</strong> ha modificato una prenotazione.<br><br>
         <div style="background-color: #fee; padding: 15px; border-left: 4px solid #f00; margin: 10px 0;">
           <strong style="color: #c00;">DATI PRECEDENTI:</strong><br>
           Stanza: ${oldRoomName}<br>
           Data: ${formatDateForDisplay(editingBooking.date)}<br>
           Orario: dalle ${editingBooking.startTime.substring(0, 5)} alle ${editingBooking.endTime.substring(0, 5)}<br>
           Note: ${editingBooking.note || 'Nessuna'}
         </div>
         <div style="background-color: #efe; padding: 15px; border-left: 4px solid #0a0; margin: 10px 0;">
           <strong style="color: #0a0;">NUOVI DATI:</strong><br>
           Stanza: ${roomName}<br>
           Data: ${formatDateForDisplay(bookingForm.date)}<br>
           Orario: dalle ${bookingForm.startTime} alle ${bookingForm.endTime}<br>
           Note: ${bookingForm.note || 'Nessuna'}
         </div>
         Utente: ${currentUser.email}`
      );
      
      setEditingBooking(null);
      setBookingForm({ 
        date: '', 
        startTime: '09:00', 
        endTime: '09:30', 
        note: '', 
        room_id: null,
        admin_notes: ''
      });
      showAlert('Prenotazione aggiornata!');
    } catch (error) {
      showAlert('Errore aggiornamento prenotazione');
    } finally {
      setLoading(false);
    }
  };

const handleDeleteBooking = async (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) return;
    
    if (currentUser.isAdmin) {
      showConfirm('Eliminare prenotazione?', async () => {
        try {
          setLoading(true);
          await apiCall(`bookings.php?id=${bookingId}`, {
            method: 'DELETE'
          });
          await loadBookings();
          
          // Invia email all'admin con dettagli completi
          const roomName = getRoomById(booking.room_id)?.name || 'la sala';
          const user = users.find(u => u.id === booking.userId);
          sendEmail(
            config.ADMIN_EMAIL,
            'Prenotazione Cancellata (da Admin)',
            `Hai cancellato una prenotazione come amministratore.<br><br>
             <div style="background-color: #fee; padding: 15px; border-left: 4px solid #f00; margin: 10px 0;">
               <strong>PRENOTAZIONE CANCELLATA:</strong><br>
               Stanza: ${roomName}<br>
               Data: ${formatDateForDisplay(booking.date)}<br>
               Orario: dalle ${booking.startTime.substring(0, 5)} alle ${booking.endTime.substring(0, 5)}<br>
               Prenotata da: <strong>${booking.userName}</strong><br>
               Email utente: ${user?.email || 'N/D'}<br>
               Note: ${booking.note || 'Nessuna'}
               ${booking.adminNotes ? `<br>Note Admin: ${booking.adminNotes}` : ''}
             </div>`
          );
          
          showAlert('Eliminata!');
        } catch (error) {
          showAlert('Errore eliminazione');
        } finally {
          setLoading(false);
        }
      });
      return;
    }
    
    if (booking.userId === currentUser.id) {
      const dt = new Date(`${booking.date}T${booking.startTime}`);
      const hrs = (dt - new Date()) / (1000 * 60 * 60);
      
      if (hrs > 0 && hrs < config.BOOKING_CANCELLATION_HOURS) {
        showAlert(`Cancellazione possibile solo fino a ${config.BOOKING_CANCELLATION_HOURS}h prima`);
        return;
      }
      
      showConfirm('Eliminare prenotazione?', async () => {
        try {
          setLoading(true);
          await apiCall(`bookings.php?id=${bookingId}`, {
            method: 'DELETE'
          });
          await loadBookings();
          
          // Invia email di notifica all'amministratore
          const roomName = getRoomById(booking.room_id)?.name || 'la sala';
          sendEmail(
            config.ADMIN_EMAIL,
            'Prenotazione Cancellata',
            `<strong>${currentUser.nome} ${currentUser.cognome}</strong> ha cancellato una prenotazione.<br><br>
             <div style="background-color: #fee; padding: 15px; border-left: 4px solid #f00; margin: 10px 0;">
               <strong>PRENOTAZIONE CANCELLATA:</strong><br>
               Stanza: ${roomName}<br>
               Data: ${formatDateForDisplay(booking.date)}<br>
               Orario: dalle ${booking.startTime.substring(0, 5)} alle ${booking.endTime.substring(0, 5)}<br>
               Prenotata da: <strong>${booking.userName}</strong><br>
               Email utente: ${currentUser.email}<br>
               Note: ${booking.note || 'Nessuna'}
             </div>`
          );
          
          showAlert('Eliminata!');
        } catch (error) {
          showAlert('Errore eliminazione');
        } finally {
          setLoading(false);
        }
      });
    }
  };

  const startEditBooking = (booking) => {
    const cleanStartTime = booking.startTime.substring(0, 5);
    const cleanEndTime = booking.endTime.substring(0, 5);
    
    if (currentUser.isAdmin) {
      setEditingBooking(booking);
      setBookingForm({
        date: booking.date,
        startTime: cleanStartTime,
        endTime: cleanEndTime,
        note: booking.note || '',
        room_id: booking.room_id,
        admin_notes: booking.admin_notes || ''
      });
      setShowBookingForm(false);
      const [year, month, day] = booking.date.split('-');
      setSelectedDate(new Date(year, month - 1, day));
      return;
    }
    
    if (booking.userId === currentUser.id) {
      const dt = new Date(`${booking.date}T${booking.startTime}`);
      const hrs = (dt - new Date()) / (1000 * 60 * 60);
      
      if (hrs > 0 && hrs < config.BOOKING_EDIT_HOURS) {
        showAlert(`Modifica possibile solo fino a ${config.BOOKING_EDIT_HOURS}h prima`);
        return;
      }
      
      setEditingBooking(booking);
      setBookingForm({
        date: booking.date,
        startTime: cleanStartTime,
        endTime: cleanEndTime,
        note: booking.note || '',
        room_id: booking.room_id,
        admin_notes: ''
      });
      setShowBookingForm(false);
      const [year, month, day] = booking.date.split('-');
      setSelectedDate(new Date(year, month - 1, day));
    }
  };

// ==================== FINE PARTE 1 ====================

// ==================== APP.JS FINALE MULTI-STANZA - PARTE 2 DI 3 ====================
// IMPORTANTE: Questa parte va DOPO la Parte 1

  // VISTA LOGIN E REGISTRAZIONE
  if (view === 'login' || view === 'register') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="flex items-center justify-center mb-6">
            <Calendar className="w-12 h-12 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">{config.APP_NAME}</h1>
          
          {showResetPassword ? (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-center mb-4">Reimposta Password</h2>
              <p className="text-sm text-gray-600 text-center mb-4">
                Inserisci la tua email per ricevere una nuova password temporanea
              </p>
              <input 
                type="email" 
                placeholder="Email" 
                className="w-full px-4 py-2 border rounded-lg" 
                value={resetPasswordEmail}
                onChange={(e) => setResetPasswordEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleResetPassword()}
                disabled={loading} 
              />
              <button 
                onClick={handleResetPassword} 
                disabled={loading} 
                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Caricamento...' : 'Invia Nuova Password'}
              </button>
              <button 
                onClick={() => {
                  setShowResetPassword(false);
                  setResetPasswordEmail('');
                }} 
                disabled={loading}
                className="w-full text-indigo-600 py-2 hover:bg-indigo-50 rounded-lg"
              >
                Torna al Login
              </button>
            </div>
          ) : view === 'login' ? (
            <div className="space-y-4">
              <input 
                type="email" 
                placeholder="Email" 
                className="w-full px-4 py-2 border rounded-lg" 
                value={loginForm.email}
                onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                disabled={loading} 
              />
              <input 
                type="password" 
                placeholder="Password" 
                className="w-full px-4 py-2 border rounded-lg" 
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                disabled={loading} 
              />
              <button 
                onClick={handleLogin} 
                disabled={loading} 
                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Caricamento...' : 'Accedi'}
              </button>
              <button 
                onClick={() => setShowResetPassword(true)} 
                disabled={loading}
                className="w-full text-sm text-indigo-600 py-2 hover:bg-indigo-50 rounded-lg"
              >
                Password dimenticata?
              </button>
              <button 
                onClick={() => setView('register')} 
                disabled={loading}
                className="w-full text-indigo-600 py-2 hover:bg-indigo-50 rounded-lg"
              >
                Registrati
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <input 
                type="text" 
                placeholder="Nome *" 
                className="w-full px-4 py-2 border rounded-lg" 
                value={registerForm.nome}
                onChange={(e) => setRegisterForm({...registerForm, nome: e.target.value})} 
                disabled={loading} 
              />
              <input 
                type="text" 
                placeholder="Cognome *" 
                className="w-full px-4 py-2 border rounded-lg" 
                value={registerForm.cognome}
                onChange={(e) => setRegisterForm({...registerForm, cognome: e.target.value})} 
                disabled={loading} 
              />
              <input 
                type="text" 
                placeholder="Professione" 
                className="w-full px-4 py-2 border rounded-lg" 
                value={registerForm.professione}
                onChange={(e) => setRegisterForm({...registerForm, professione: e.target.value})} 
                disabled={loading} 
              />
              <input 
                type="email" 
                placeholder="Email *" 
                className="w-full px-4 py-2 border rounded-lg" 
                value={registerForm.email}
                onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})} 
                disabled={loading} 
              />
              <input 
                type="tel" 
                placeholder="Cellulare *" 
                className="w-full px-4 py-2 border rounded-lg" 
                value={registerForm.cellulare}
                onChange={(e) => setRegisterForm({...registerForm, cellulare: e.target.value})} 
                disabled={loading} 
              />
              <input 
                type="password" 
                placeholder="Password" 
                className="w-full px-4 py-2 border rounded-lg" 
                value={registerForm.password}
                onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})} 
                disabled={loading} 
              />
              <button 
                onClick={handleRegister} 
                disabled={loading}
                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Caricamento...' : 'Registrati'}
              </button>
              <button 
                onClick={() => setView('login')} 
                disabled={loading}
                className="w-full text-indigo-600 py-2 hover:bg-indigo-50 rounded-lg"
              >
                Accedi
              </button>
            </div>
          )}
        </div>

        {alertDialog.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <p className="text-gray-800 whitespace-pre-line mb-4">{alertDialog.message}</p>
              <button 
                onClick={() => setAlertDialog({ show: false, message: '' })}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // VISTA ADMIN
  if (view === 'admin') {
    const pending = users.filter(u => !u.isApproved && !u.isAdmin);
    const approved = users.filter(u => u.isApproved || u.isAdmin);

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold">Pannello Admin</h1>
            <div className="flex gap-4">
              <button 
                onClick={() => setView('calendar')} 
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Calendario
              </button>
              <button 
                onClick={handleLogout} 
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Esci
              </button>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">In Attesa ({pending.length})</h2>
            {pending.length === 0 ? (
              <p className="text-gray-500">Nessuno</p>
            ) : (
              <div className="space-y-3">
                {pending.map(u => (
                  <div key={u.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-semibold">{u.nome} {u.cognome}</p>
                      <p className="text-sm text-gray-600">{u.email} - {u.cellulare}</p>
                      {u.professione && <p className="text-sm text-gray-500">{u.professione}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => approveUser(u.id)} 
                        disabled={loading}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        Approva
                      </button>
                      <button 
                        onClick={() => showConfirm(
                          `Rifiutare la registrazione di ${u.nome} ${u.cognome}?\n\nL'utente verrà eliminato definitivamente.`, 
                          () => deleteUser(u.id)
                        )} 
                        disabled={loading}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        Rifiuta
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">Utenti Approvati ({approved.length})</h2>
            <div className="space-y-3">
              {approved.map(u => (
                <div key={u.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-semibold">
                      {u.nome} {u.cognome}
                      {!!u.isAdmin && (
                        <span className="ml-2 text-xs bg-indigo-600 text-white px-2 py-1 rounded">
                          Admin
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-600">{u.email}</p>
                  </div>
                  {!u.isAdmin && (
                    <div className="flex gap-2">
                      <button 
                        onClick={() => showUserInfo(u)} 
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        <Info className="w-4 h-4" />
                        Info
                      </button>
                      <button 
                        onClick={() => showConfirm('Eliminare utente?', () => deleteUser(u.id))} 
                        disabled={loading} 
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        Elimina
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">Prenotazioni ({getFilteredBookings().length})</h2>
            
            {/* Filtri */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-3">Filtri</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Data Da</label>
                  <input 
                    type="date"
                    value={adminFilters.dateFrom}
                    onChange={(e) => setAdminFilters({...adminFilters, dateFrom: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data A</label>
                  <input 
                    type="date"
                    value={adminFilters.dateTo}
                    onChange={(e) => setAdminFilters({...adminFilters, dateTo: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Utente</label>
                  <select 
                    value={adminFilters.userId}
                    onChange={(e) => setAdminFilters({...adminFilters, userId: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="all">Tutti gli utenti</option>
                    {users.filter(u => !u.isAdmin).map(user => (
                      <option key={user.id} value={user.id}>
                        {user.nome} {user.cognome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button 
                  onClick={() => {
                    setAdminFilters({ dateFrom: '', dateTo: '', userId: 'all' });
                    setSelectedBookings([]);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Resetta Filtri
                </button>
                {selectedBookings.length > 0 && (
                  <button 
                    onClick={handleDeleteSelectedBookings}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    Elimina Selezionate ({selectedBookings.length})
                  </button>
                )}
              </div>
            </div>
            
            {getFilteredBookings().length === 0 ? (
              <p className="text-gray-500">Nessuna prenotazione trovata</p>
            ) : (
              <>
                {/* Checkbox Seleziona Tutto */}
                <div className="mb-3 pb-3 border-b">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={selectedBookings.length === getFilteredBookings().length && getFilteredBookings().length > 0}
                      onChange={() => toggleSelectAllBookings(getFilteredBookings())}
                      className="w-4 h-4 text-indigo-600 rounded"
                    />
                    <span className="text-sm font-medium">
                      Seleziona Tutto ({getFilteredBookings().length})
                    </span>
                  </label>
                </div>
                
                <div className="space-y-3">
                  {getFilteredBookings().map(b => {
                    const room = getRoomById(b.room_id);
                    const isSelected = selectedBookings.includes(b.id);
                    
                    return (
                      <div 
                        key={b.id} 
                        className={`p-4 rounded-lg flex items-center gap-3 ${
                          isSelected ? 'bg-indigo-50 border-2 border-indigo-300' : 'bg-gray-50 border-2 border-transparent'
                        }`}
                      >
                        <input 
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleBookingSelection(b.id)}
                          className="w-5 h-5 text-indigo-600 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold">{b.userName}</p>
                            {room && (
                              <span 
                                className="text-xs px-2 py-1 rounded"
                                style={{ 
                                  backgroundColor: room.colorLight, 
                                  color: room.colorDark 
                                }}
                              >
                                {room.name}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {formatDateForDisplay(b.date)} - {b.startTime.substring(0, 5)}-{b.endTime.substring(0, 5)}
                          </p>
                          {b.note && <p className="text-sm text-gray-500 mt-1">{b.note}</p>}
                          {b.admin_notes && b.admin_notes !== '0' && b.admin_notes.trim() !== '' && (
                            <p className="text-xs text-red-700 mt-2 p-2 bg-red-50 rounded border border-red-200">
                              <strong>Note Admin:</strong> {b.admin_notes}
                            </p>
                          )}
                        </div>
                        <button 
                          onClick={() => handleDeleteBooking(b.id)} 
                          disabled={loading}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-40">
            <div className="bg-white rounded-lg p-6">
              <p className="text-gray-800">Caricamento...</p>
            </div>
          </div>
        )}

        {alertDialog.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <p className="text-gray-800 whitespace-pre-line mb-4">{alertDialog.message}</p>
              <button 
                onClick={() => setAlertDialog({ show: false, message: '' })}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                OK
              </button>
            </div>
          </div>
        )}

        {confirmDialog.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <p className="text-gray-800 whitespace-pre-line mb-4">{confirmDialog.message}</p>
              <div className="flex gap-3">
                <button 
                  onClick={() => { 
                    setConfirmDialog({ show: false, message: '', onConfirm: null }); 
                    if (confirmDialog.onConfirm) confirmDialog.onConfirm(); 
                  }}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Conferma
                </button>
                <button 
                  onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null })}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}

        {userInfoDialog.show && userInfoDialog.user && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800">Informazioni Utente</h3>
                <button 
                  onClick={() => setUserInfoDialog({ show: false, user: null })}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <div className="border-b pb-3">
                  <p className="text-sm text-gray-500">Nome Completo</p>
                  <p className="text-lg font-semibold">{userInfoDialog.user.nome} {userInfoDialog.user.cognome}</p>
                </div>
                <div className="border-b pb-3">
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-lg">{userInfoDialog.user.email}</p>
                </div>
                <div className="border-b pb-3">
                  <p className="text-sm text-gray-500">Cellulare</p>
                  <p className="text-lg">{userInfoDialog.user.cellulare}</p>
                </div>
                {userInfoDialog.user.professione && (
                  <div className="border-b pb-3">
                    <p className="text-sm text-gray-500">Professione</p>
                    <p className="text-lg">{userInfoDialog.user.professione}</p>
                  </div>
                )}
                <div className="border-b pb-3">
                  <p className="text-sm text-gray-500">Stato Account</p>
                  <p className="text-lg">
                    {userInfoDialog.user.isApproved ? (
                      <span className="text-green-600 font-semibold">Approvato</span>
                    ) : (
                      <span className="text-orange-600 font-semibold">In attesa</span>
                    )}
                  </p>
                </div>
                <div className="pb-3">
                  <p className="text-sm text-gray-500">ID Utente</p>
                  <p className="text-lg font-mono text-gray-600">#{userInfoDialog.user.id}</p>
                </div>
              </div>
              <button 
                onClick={() => setUserInfoDialog({ show: false, user: null })}
                className="w-full mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Chiudi
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

// ==================== FINE PARTE 2 ====================

// ==================== APP.JS FINALE MULTI-STANZA - PARTE 3 DI 3 ====================
// IMPORTANTE: Questa è l'ULTIMA parte, va DOPO la Parte 2

  // VISTA CALENDARIO
  const dateStr = (() => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  })();
  
  const dayBookings = getBookingsForDate(selectedDate, selectedRoom);
  const isWeekend = selectedDate.getDay() === 0 || selectedDate.getDay() === 6;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">
              Benvenuto, {currentUser.nome}
            </h1>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowChangePassword(true)} 
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cambia Password
              </button>
              {!!currentUser.isAdmin && (
                <button 
                  onClick={() => setView('admin')} 
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Admin
                </button>
              )}
              <button 
                onClick={handleLogout} 
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Esci
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Toggle Vista */}
        <div className="mb-6 flex gap-4">
          <button 
            onClick={() => {
              setWeekView(false);
              loadBookings(); // Refresh quando passi a vista giornaliera
            }}
            className={`px-4 py-2 rounded-lg ${!weekView ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            Vista Giornaliera
          </button>
          <button 
            onClick={() => {
              setWeekView(true);
              loadBookings(); // Refresh quando passi a vista settimanale
            }}
            className={`px-4 py-2 rounded-lg ${weekView ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            Vista Settimanale
          </button>
        </div>

        {!weekView ? (
          /* VISTA GIORNALIERA */
          <div className="bg-white rounded-lg shadow p-6">
            {/* Navigazione Data */}
            <div className="flex items-center justify-between mb-6">
              <button 
                onClick={() => changeDate(-1)} 
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-800">{formatDate(selectedDate)}</h2>
                  <button
                    onClick={() => {
                      setSelectedDate(new Date());
                      loadBookings();
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                  >
                    Oggi
                  </button>
                </div>
                <input 
                  type="date" 
                  value={dateStr}
                  onChange={(e) => {
                    const [year, month, day] = e.target.value.split('-');
                    const newDate = new Date(year, month - 1, day);
                    setSelectedDate(newDate);
                  }}
                  className="mt-2 px-3 py-1 border rounded"
                />
              </div>
              <button 
                onClick={() => changeDate(1)} 
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Selettore Stanza - SOLO STANZE ABILITATE */}
            {getEnabledRooms().length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Seleziona Stanza:</label>
                <div className="flex gap-3">
                  {getEnabledRooms().map(room => (
                    <button
                      key={room.id}
                      onClick={() => setSelectedRoom(room.id)}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                        selectedRoom === room.id
                          ? 'border-current font-semibold shadow-md'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{
                        color: selectedRoom === room.id ? room.color : '#6b7280',
                        backgroundColor: selectedRoom === room.id ? room.colorLight : 'white'
                      }}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: room.color }}
                        ></div>
                        <span>{room.name}</span>
                      </div>
                      <div className="text-xs mt-1 opacity-75">
                        Capienza: {room.capacity} persone
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messaggio se nessuna stanza abilitata */}
            {getEnabledRooms().length === 0 && (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-xl text-gray-600">Nessuna sala disponibile</p>
                <p className="text-sm text-gray-500 mt-2">Contatta l'amministratore</p>
              </div>
            )}

            {isWeekend ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-xl text-gray-600">Weekend - Sala chiusa</p>
              </div>
            ) : getEnabledRooms().length > 0 ? (
              <>
                {/* Pulsante Nuova Prenotazione */}
                {!showBookingForm && !editingBooking && (
                  <button 
                    onClick={() => {
                      const enabledRooms = getEnabledRooms();
                      setShowBookingForm(true);
                      setBookingForm({ 
                        date: dateStr, 
                        startTime: '09:00', 
                        endTime: '09:30', 
                        note: '',
                        room_id: selectedRoom && selectedRoom !== 'all' ? selectedRoom : null, // Usa selectedRoom solo se valido
                        repeatWeekly: false,
                        repeatWeeks: 4
                      });
                    }}
                    className="w-full mb-6 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Nuova Prenotazione {getEnabledRooms().length > 0 && `- ${getRoomById(selectedRoom)?.name}`}
                  </button>
                )}

                {/* Form Prenotazione */}
                {(showBookingForm || editingBooking) && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="text-lg font-bold mb-4">
                      {editingBooking ? 'Modifica Prenotazione' : 'Nuova Prenotazione'}
                    </h3>
                    <div className="space-y-4">
                      {/* Data */}
                      <div className="bg-indigo-50 p-3 rounded-lg mb-2">
                        <div className="flex items-center gap-2 text-indigo-700">
                          <Calendar className="w-4 h-4" />
                          <span className="font-semibold">Data: {formatDateForDisplay(bookingForm.date)}</span>
                        </div>
                        <p className="text-xs text-indigo-600 mt-1">
                          Usa le frecce o il calendario per cambiare data
                        </p>
                      </div>

                      {/* Stanza selezionata */}
                      {getEnabledRooms().length > 1 && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Stanza Selezionata *</label>
                          {bookingForm.room_id && bookingForm.room_id !== 'all' ? (
                            <div 
                              className="w-full px-3 py-2 border rounded-lg"
                              style={{
                                backgroundColor: getRoomById(bookingForm.room_id)?.colorLight || '#f3f4f6'
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: getRoomById(bookingForm.room_id)?.color }}
                                ></div>
                                <span className="font-medium">
                                  {getRoomById(bookingForm.room_id)?.name}
                                </span>
                                <span className="text-sm text-gray-600">
                                  (max {getRoomById(bookingForm.room_id)?.capacity} persone)
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full px-3 py-2 border-2 border-red-300 rounded-lg bg-red-50">
                              <span className="text-red-700 font-medium">
                                ⚠️ Nessuna stanza selezionata - Usa i pulsanti sopra
                              </span>
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Usa i pulsanti sopra per cambiare stanza
                          </p>
                        </div>
                      )}
                      {/* Se c'è solo una stanza, mostrala come info */}
                      {getEnabledRooms().length === 1 && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Stanza</label>
                          <div className="p-3 bg-gray-50 rounded-lg border">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: getEnabledRooms()[0].color }}
                              ></div>
                              <span className="font-medium">{getEnabledRooms()[0].name}</span>
                              <span className="text-sm text-gray-600">
                                (max {getEnabledRooms()[0].capacity} persone)
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Orari */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Ora Inizio</label>
                          <select 
                            value={bookingForm.startTime}
                            onChange={(e) => {
                              const newStartTime = e.target.value;
                              const startMinutes = timeToMinutes(newStartTime);
                              const endMinutes = startMinutes + 60; // Aggiungi 1 ora
                              const newEndTime = minutesToTime(endMinutes);
                              
                              setBookingForm({
                                ...bookingForm, 
                                startTime: newStartTime,
                                endTime: newEndTime
                              });
                            }}
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            {generateTimeSlots().map(slot => (
                              <option key={slot} value={slot}>{slot}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Ora Fine</label>
                          <select 
                            value={bookingForm.endTime}
                            onChange={(e) => setBookingForm({...bookingForm, endTime: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            {generateEndTimeSlots().map(slot => (
                              <option key={slot} value={slot}>{slot}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Note */}
                      <div>
                        <label className="block text-sm font-medium mb-1">Note</label>
                        <textarea 
                          value={bookingForm.note}
                          onChange={(e) => setBookingForm({...bookingForm, note: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          rows="2"
                          placeholder="Note opzionali..."
                        />
                      </div>
                      {/* Note Amministratore - Solo per Admin */}
                      {!!currentUser.isAdmin && (
                        <div className="border-t pt-4">
                          <label className="block text-sm font-medium mb-1 text-red-700">
                            Note Amministratore (visibili solo a te)
                          </label>
                          <textarea 
                            value={bookingForm.admin_notes}
                            onChange={(e) => setBookingForm({...bookingForm, admin_notes: e.target.value})}
                            className="w-full px-3 py-2 border border-red-300 rounded-lg bg-red-50"
                            rows="2"
                            placeholder="Note riservate all'amministratore..."
                          />
                        </div>
                      )}
                      {/* Ripeti Settimanalmente */}
                      <div className="border-t pt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox"
                            checked={bookingForm.repeatWeekly}
                            onChange={(e) => setBookingForm({...bookingForm, repeatWeekly: e.target.checked})}
                            className="w-4 h-4 text-indigo-600 rounded"
                          />
                          <span className="text-sm font-medium">Ripeti settimanalmente</span>
                        </label>
                        
                        {bookingForm.repeatWeekly && (
                          <div className="mt-3 ml-6">
                            <label className="block text-sm font-medium mb-1">Per quante settimane?</label>
                            <select 
                              value={bookingForm.repeatWeeks}
                              onChange={(e) => setBookingForm({...bookingForm, repeatWeeks: parseInt(e.target.value)})}
                              className="w-full px-3 py-2 border rounded-lg"
                            >
                              {[2, 3, 4, 5, 6, 7, 8, 10, 12].map(weeks => (
                                <option key={weeks} value={weeks}>{weeks} settimane</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              Verranno create {bookingForm.repeatWeeks} prenotazioni consecutive ogni {['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'][new Date(bookingForm.date).getDay()]}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Pulsanti */}
                      <div className="flex gap-3">
                        {editingBooking ? (
                          <>
                            <button 
                              onClick={handleUpdateBooking}
                              disabled={loading}
                              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              <Check className="w-4 h-4" />
                              Salva
                            </button>
                            <button 
                              onClick={() => {
                                setEditingBooking(null);
                                setBookingForm({ date: '', startTime: '09:00', endTime: '09:30', note: '', room_id: null });
                              }}
                              disabled={loading}
                              className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              <X className="w-4 h-4" />
                              Annulla
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              onClick={handleCreateBooking}
                              disabled={loading}
                              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              <Check className="w-4 h-4" />
                              Crea
                            </button>
                            <button 
                              onClick={() => {
                                setShowBookingForm(false);
                                setBookingForm({ date: '', startTime: '09:00', endTime: '09:30', note: '', room_id: null });
                              }}
                              disabled={loading}
                              className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              <X className="w-4 h-4" />
                              Annulla
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Lista Prenotazioni CON COLORI STANZE */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold">Prenotazioni ({dayBookings.length})</h3>
                  {dayBookings.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Nessuna prenotazione</p>
                  ) : (
                    dayBookings.map(booking => {
                      const isOwnBooking = booking.userId === currentUser.id;
                      const canSeeDetails = currentUser.isAdmin || isOwnBooking;
                      const room = getRoomById(booking.room_id);
                      
                      return (
                        <div 
                          key={booking.id} 
                          className="p-4 rounded-lg border-l-4"
                          style={{
                            backgroundColor: room ? room.colorLight : '#f3f4f6',
                            borderLeftColor: room ? room.color : '#d1d5db'
                          }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              {/* Badge stanza */}
                              {room && (
                                <div className="flex items-center gap-2 mb-2">
                                  <div 
                                    className="w-2 h-2 rounded-full" 
                                    style={{ backgroundColor: room.color }}
                                  ></div>
                                  <span className="text-xs font-semibold" style={{ color: room.colorDark }}>
                                    {room.name}
                                  </span>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <span className="font-semibold">
                                  {booking.startTime.substring(0, 5)} - {booking.endTime.substring(0, 5)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                {canSeeDetails ? booking.userName : 'Occupato'}
                              </p>
                              {canSeeDetails && booking.note && (
                                <p className="text-sm text-gray-500 mt-1 italic">{booking.note}</p>
                              )}
                              {!!currentUser.isAdmin && booking.admin_notes && booking.admin_notes !== '0' && booking.admin_notes.trim() !== '' && (
                                <p className="text-xs text-red-700 mt-2 p-2 bg-red-50 rounded border border-red-200">
                                  <strong>Note Admin:</strong> {booking.admin_notes}
                                </p>
                              )}
                            </div>
                            {(isOwnBooking || currentUser.isAdmin) && (
                              <div className="flex gap-2">
                                <button 
                                  onClick={() => startEditBooking(booking)}
                                  disabled={loading}
                                  className="p-2 text-blue-600 hover:bg-blue-100 rounded disabled:opacity-50"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteBooking(booking.id)}
                                  disabled={loading}
                                  className="p-2 text-red-600 hover:bg-red-100 rounded disabled:opacity-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : null}
          </div>
        ) : (
          /* VISTA SETTIMANALE CON MULTI-STANZA */
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <button 
                onClick={() => changeWeek(-1)} 
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-800">
                  Settimana del {formatDate(currentWeekStart)}
                </h2>
                <button
                  onClick={() => {
                    const today = new Date();
                    setCurrentWeekStart(getMonday(today));
                    setSelectedDate(today);
					// Refresh prenotazioni quando clicchi Oggi
                    loadBookings();
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                >
                  Oggi
                </button>
              </div>
              <button 
                onClick={() => changeWeek(1)} 
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Pulsante Nuova Prenotazione */}
            {!showBookingForm && !editingBooking && getEnabledRooms().length > 0 && (
              <button 
                onClick={() => {
                  const currentDateStr = (() => {
                    const year = selectedDate.getFullYear();
                    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                    const day = String(selectedDate.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  })();
                  const enabledRooms = getEnabledRooms();
                  setShowBookingForm(true);
                  setWeekView(false);
                  setBookingForm({ 
                    date: currentDateStr, 
                    startTime: '09:00', 
                    endTime: '09:30', 
                    note: '',
                    room_id: null, // CAMBIATO: forza la selezione
                    repeatWeekly: false,
                    repeatWeeks: 4
                  });
                }}
                className="w-full mb-6 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Nuova Prenotazione
              </button>
            )}

            {/* Selettore Stanza - SOLO STANZE ABILITATE */}
            {getEnabledRooms().length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Visualizza:</label>
                <div className="flex gap-3 flex-wrap">
                  {getEnabledRooms().length > 1 && (
                    <button
                      onClick={() => setSelectedRoom('all')}
                      className={`px-4 py-2 rounded-lg border-2 ${
                        selectedRoom === 'all'
                          ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-semibold'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      Tutte
                    </button>
                  )}
                  {getEnabledRooms().map(room => (
                    <button
                      key={room.id}
                      onClick={() => setSelectedRoom(room.id)}
                      className={`px-4 py-2 rounded-lg border-2 ${
                        selectedRoom === room.id
                          ? 'border-current font-semibold'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{
                        color: selectedRoom === room.id ? room.color : '#6b7280',
                        backgroundColor: selectedRoom === room.id ? room.colorLight : 'white',
                        borderColor: selectedRoom === room.id ? room.color : '#e5e7eb'
                      }}
                    >
                      {room.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messaggio se nessuna stanza abilitata */}
            {getEnabledRooms().length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-xl text-gray-600">Nessuna sala disponibile</p>
                <p className="text-sm text-gray-500 mt-2">Contatta l'amministratore</p>
              </div>
            ) : (
              <>
                {/* Griglia Calendario Settimanale */}
                <div className="overflow-x-auto">
                  <div className="inline-block min-w-full">
                    <div className="grid grid-cols-6 gap-0 border-b-2 border-gray-300">
                      <div className="p-2 bg-gray-50 border-r border-gray-200 font-semibold text-sm">
                        Orario
                      </div>
                      {getWeekDays().map(day => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const isToday = day.getTime() === today.getTime();
                        
                        return (
                          <div 
                            key={day.toISOString()} 
                            className={`p-2 border-r border-gray-200 text-center ${
                              isToday ? 'bg-indigo-100' : 'bg-gray-50'
                            }`}
                          >
                            <div className={`font-bold text-sm ${isToday ? 'text-indigo-700' : ''}`}>
                              {formatDate(day).split(' ')[0]}
                            </div>
                            <div className={`text-xs ${isToday ? 'text-indigo-600' : 'text-gray-600'}`}>
                              {formatDate(day).split(' ').slice(1).join(' ')}
                            </div>
                            {isToday && (
                              <div className="text-xs font-semibold text-indigo-700 mt-1">OGGI</div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="relative">
                      {Array.from({ length: 21 }, (_, i) => {
                        const hour = Math.floor((config.WORKING_HOURS_START + i * 30) / 60);
                        const minute = (config.WORKING_HOURS_START + i * 30) % 60;
                        const timeSlot = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                        
                        return (
                          <div key={timeSlot} className="grid grid-cols-6 gap-0 border-b border-gray-200">
                            <div className="p-2 bg-gray-50 border-r border-gray-200 text-xs font-medium text-gray-600">
                              {timeSlot}
                            </div>
                            
                            {getWeekDays().map(day => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const isToday = day.getTime() === today.getTime();
                              
                              let dayBookingsWeek = getBookingsForDate(day);
                              
                              if (selectedRoom && selectedRoom !== 'all') {
                                dayBookingsWeek = dayBookingsWeek.filter(b => b.room_id === selectedRoom);
                              }
                              
                              const slotStartMinutes = config.WORKING_HOURS_START + i * 30;
                              const bookingsStartingHere = dayBookingsWeek.filter(booking => {
                                const bookingStartMinutes = timeToMinutes(booking.startTime);
                                return bookingStartMinutes >= slotStartMinutes && bookingStartMinutes < slotStartMinutes + 30;
                              });

                              // Per ogni prenotazione che inizia qui, trova tutte le prenotazioni
                              // del giorno che si sovrappongono ad essa in qualsiasi momento della sua durata.
                              // Questo garantisce che anche con orari di inizio diversi i blocchi si dividano correttamente.
                              const getOverlapGroup = (booking) => {
                                const bStart = timeToMinutes(booking.startTime);
                                const bEnd = timeToMinutes(booking.endTime);
                                return dayBookingsWeek.filter(other => {
                                  const oStart = timeToMinutes(other.startTime);
                                  const oEnd = timeToMinutes(other.endTime);
                                  return oStart < bEnd && oEnd > bStart;
                                }).sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime) || a.id.localeCompare(b.id));
                              };

                              return (
                                <div 
                                  key={`${day.toISOString()}-${timeSlot}`} 
                                  className={`relative border-r border-gray-200 min-h-[60px] ${
                                    isToday ? 'bg-indigo-50' : ''
                                  }`}
                                >
								  {bookingsStartingHere.map((booking) => {
									const isOwnBooking = booking.userId === currentUser.id;
									const canSeeDetails = currentUser.isAdmin || isOwnBooking;
									const room = getRoomById(booking.room_id);
									const bookingStartMinutes = timeToMinutes(booking.startTime);
									const bookingEndMinutes = timeToMinutes(booking.endTime);
									
									const minutesFromSlotStart = bookingStartMinutes - slotStartMinutes;
									const topOffset = (minutesFromSlotStart / 30) * config.CELL_HEIGHT_PX;
									const durationMinutes = bookingEndMinutes - bookingStartMinutes;
									const heightPx = (durationMinutes / 30) * config.CELL_HEIGHT_PX - 4;
									
									// Calcola larghezza e posizione considerando TUTTE le prenotazioni
									// che si sovrappongono in qualsiasi momento della durata di questa
									const overlapGroup = getOverlapGroup(booking);
									const totalOverlapping = overlapGroup.length;
									const colIdx = overlapGroup.findIndex(b => b.id === booking.id);
									const widthPercent = totalOverlapping > 1 ? (100 / totalOverlapping) : 100;
									const leftPercent = totalOverlapping > 1 ? (colIdx * widthPercent) : 0;
									
									return (
									  <div
										key={booking.id}
										className="absolute rounded p-1 text-xs overflow-hidden shadow-sm border-l-4"
										style={{ 
										  top: `${topOffset + 2}px`,
										  height: `${heightPx}px`,
										  left: `${leftPercent}%`,
										  width: `${widthPercent}%`,
										  zIndex: 10,
										  backgroundColor: room ? room.color : '#9ca3af',
										  borderLeftColor: room ? room.colorDark : '#4b5563',
										  color: 'white',
										  paddingLeft: '4px',
										  paddingRight: '2px'
										}}
									  >
										{selectedRoom === 'all' && room && (
										  <div className="text-[9px] font-bold mb-1 opacity-90 truncate">
											{room.name}
										  </div>
										)}
										<div className="font-semibold text-[10px] truncate">
										  {booking.startTime.substring(0, 5)}-{booking.endTime.substring(0, 5)}
										</div>
										{heightPx > 35 && (
										  <div className="truncate text-[9px] mt-1">
											{canSeeDetails ? booking.userName : 'Occupato'}
										  </div>
										)}
										{canSeeDetails && booking.note && heightPx > 55 && (
										  <div className="truncate text-[9px] mt-1 opacity-90">
											{booking.note}
										  </div>
										)}
									  </div>
									);
								  })}
								</div>
							  );
							})}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Legenda - SOLO STANZE ABILITATE */}
                <div className="mt-6 flex gap-6 justify-center text-sm flex-wrap">
                  {getEnabledRooms().map(room => (
                    <div key={room.id} className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: room.color }}></div>
                      <span>{room.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Dialog comuni */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-6">
            <p className="text-gray-800">Caricamento...</p>
          </div>
        </div>
      )}

      {alertDialog.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <p className="text-gray-800 whitespace-pre-line mb-4">{alertDialog.message}</p>
            <button 
              onClick={() => setAlertDialog({ show: false, message: '' })}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {confirmDialog.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <p className="text-gray-800 whitespace-pre-line mb-4">{confirmDialog.message}</p>
            <div className="flex gap-3">
              <button 
                onClick={() => { 
                  setConfirmDialog({ show: false, message: '', onConfirm: null }); 
                  if (confirmDialog.onConfirm) confirmDialog.onConfirm(); 
                }}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Conferma
              </button>
              <button 
                onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null })}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Dialog Cambio Password */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cambia Password</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Password Attuale</label>
                <input 
                  type="password"
                  value={changePasswordForm.currentPassword}
                  onChange={(e) => setChangePasswordForm({...changePasswordForm, currentPassword: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Inserisci password attuale"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nuova Password</label>
                <input 
                  type="password"
                  value={changePasswordForm.newPassword}
                  onChange={(e) => setChangePasswordForm({...changePasswordForm, newPassword: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Minimo 6 caratteri"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Conferma Nuova Password</label>
                <input 
                  type="password"
                  value={changePasswordForm.confirmPassword}
                  onChange={(e) => setChangePasswordForm({...changePasswordForm, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Ripeti nuova password"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button 
                onClick={handleChangePassword}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Caricamento...' : 'Conferma'}
              </button>
              <button 
                onClick={() => {
                  setShowChangePassword(false);
                  setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      {emailStatus && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg">
          {emailStatus}
        </div>
      )}
    </div>
  );
}
